import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  Crop,
  Download,
  Ellipsis,
  FolderPlus,
  Plus,
  X,
} from 'lucide-react'
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router'
import { showDate } from '../../shared/tree'
import { ActionMenu } from '@/components/ActionMenu'
import { ConfirmButton } from '@/components/ConfirmButton'
import { CropPhoto } from '@/components/CropPhoto'
import { DatePicker } from '@/components/DatePicker'
import { FormError } from '@/components/FormError'
import { OriginalPhoto } from '@/components/OriginalPhoto'
import { NewAlbum } from '@/components/NewAlbum'
import { PersonSelect } from '@/components/PersonSelect'
import { SendPhotos } from '@/components/SendTo'
import { ShareWith } from '@/components/ShareWith'
import { Avatar } from '@/components/tree/Avatar'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { api } from '@/lib/api'
import {
  addedOn,
  afterPhotoEdit,
  albumPhotoUrl,
  organize,
  photoQuery,
  photoSrcSet,
  splitTags,
  usePhoto,
  type Organize,
  type Photo,
  whoSees,
} from '@/lib/albums'
import { audienceBody, audienceFrom, audienceSaid } from '@/lib/spaces'
import { useFamily } from '@/lib/families'
import { useItemHome, usePageFrame } from '@/lib/frame'
import { saveCrop } from '@/lib/photos'
import { useTree } from '@/lib/tree'
import { cn } from '@/lib/utils'
import { Loader, ScreenLoader } from '@/components/Loader'
import { rubber, velocity } from '@/lib/motion'
import { coverPage, fromTile, smooth, takeOrigin, tileOf, tileRadius } from '@/lib/photoMotion'

export function PhotoPage({ over = false }: { over?: boolean }) {
  const { id = '' } = useParams()
  const photo = usePhoto(id)
  // Over a page, the page stays in sight while the photo arrives; on its own, the dark room waits for it.
  if (photo.isPending)
    return over ? (
      <div className="fixed inset-0 z-50 grid place-items-center">
        <Loader label="Opening the photo" />
      </div>
    ) : (
      <ScreenLoader surface="viewer" />
    )
  if (photo.isError) return <FormError error={photo.error} />
  return <PhotoView photo={photo.data} over={over} arriving={photo.isPlaceholderData} />
}

// How wide the photo shows: on a big screen, the whole stage for a wide photo, less for a tall one held to the stage's
// height; on a phone or tablet, the whole width. A phone gets the medium copy and a big screen the display copy, each sharp on its own screen.
const viewerSizes = ({ width, height }: { width: number; height: number }) => {
  const ratio = width / height
  return `(min-width: 1024px) min(calc(100vw - 28rem), ${Math.round(85 * ratio)}vh), 100vw`
}

const round =
  'grid size-11 place-items-center rounded-full outline-none transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/60'
// On a phone or tablet the buttons float on the photo's top corners, on frosted rounds that read over any picture.
const floating =
  'max-lg:pointer-events-auto max-lg:bg-viewer/45 max-lg:text-viewer-foreground max-lg:backdrop-blur-md max-lg:hover:bg-viewer/65'

// Where a photo lives, to go back to: its album, or the photos in no album.
const whereFrom = (photo: Photo) =>
  photo.album
    ? { to: `/album/${photo.album.id}`, title: photo.album.title }
    : { to: `/circle/${photo.familyId}/photos?loose=1`, title: 'Not in an album' }

// The curves photos move on (styles/motion.css names the same ones): out of the way quickly,
const IN_SOFT = 'cubic-bezier(0.4, 0, 0.9, 0.6)'
// Pinterest's curves (from their Gestalt design system): arriving, and settling with a little overshoot. A slide across starts
// moving at once (Pinterest's own carousel curve eases in, which felt slow on a phone).
const ENTER = 'cubic-bezier(0.05, 0.7, 0.1, 1)'
const SWIFT = 'cubic-bezier(0.2, 0, 0, 1)'
const BOUNCE = 'cubic-bezier(0, 0.35, 0, 1.25)'
// The space between one photo and the next as they slide past each other.
const GAP = 24
// The photo whole, with its own rounded corners, as the clip it opens to and closes from.
const shown = (el: HTMLElement) => `inset(0px 0px round ${getComputedStyle(el).borderTopLeftRadius})`

// The frame around a photo opened on its own (from a link), with the phone's back bar pointing at its album. Over a
// page, the page keeps its own frame.
function PageFrame({ photo, title, to }: { photo: Photo; title: string; to: string }) {
  usePageFrame(useItemHome(photo, { familyId: photo.familyId, ...photo.albumShared }), {
    title,
    back: { to, label: `Back to ${title}` },
  })
  return null
}

// One photo, on its own in a dark room so nothing competes with it, with what the family knows about it beside it. On
// a phone or tablet it's the page's own colour instead, the photo across the whole width right under the clock, as
// Pinterest's are, its buttons floating on its top corners and what's known about it below. Opened from a page, it grows out of the spot where it sat and shrinks back into it when
// closed. Swipe, the arrow keys or the arrows step through the album, the photo sliding across; on a phone, pull it
// down to put it back. Escape closes it.
function PhotoView({ photo, over, arriving }: { photo: Photo; over: boolean; arriving: boolean }) {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const [params] = useSearchParams()
  // Captioning one photo after another, from the walk-through after adding photos.
  const guided = params.get('caption') === '1'
  const where = whereFrom(photo)
  const title = photo.caption || (photo.album ? `A photo from ${photo.album.title}` : 'A photo')
  const albumTo = where.to
  const room = useRef<HTMLElement>(null)
  const stage = useRef<HTMLDivElement>(null)
  const image = useRef<HTMLImageElement>(null)
  // The side the next photo comes in from, while it's on its way, how it goes (a slide from a tap, or carrying on
  // from a finger), and whether it's closing.
  const coming = useRef<{ direction: 1 | -1; from: number; flung: boolean; away: number } | null>(null)
  const [leaving, setLeaving] = useState(false)
  // The copy already on screen where it was tapped, shown under the photo until its own sharper copy arrives.
  const [stand, setStand] = useState<string | null>(null)

  // Stepping replaces the history entry, keeping the page underneath, so closing returns to it.
  const go = (id: string | null, caption = guided) =>
    id && navigate(`/photo/${id}${caption ? '?caption=1' : ''}`, { replace: true, state: location.state })

  // Like a carousel, as Pinterest's do: the photo slides off to the side and the next slides in right behind it,
  // the two moving together a little apart. The one leaving is a copy left in its place (the photo itself becomes
  // the next one); useLayoutEffect below moves them both. From a drag, they carry on from where the finger let go.
  const step = (id: string | null, direction: 1 | -1) => {
    const el = image.current
    if (!id) return
    if (!el || !smooth()) return go(id)
    for (const a of el.getAnimations()) a.cancel()
    const from = new DOMMatrix(getComputedStyle(el).transform).m41
    const ghost = el.cloneNode() as HTMLImageElement
    ghost.removeAttribute('id')
    ghost.alt = ''
    ghost.setAttribute('aria-hidden', 'true')
    ghost.dataset.leaving = ''
    Object.assign(ghost.style, {
      position: 'absolute',
      left: `${el.offsetLeft}px`,
      top: `${el.offsetTop}px`,
      width: `${el.offsetWidth}px`,
      height: `${el.offsetHeight}px`,
      transform: `translateX(${from}px)`,
      pointerEvents: 'none',
    })
    el.parentElement?.append(ghost)
    el.style.transform = ''
    coming.current = { direction, from, flung: from !== 0, away: el.offsetWidth }
    go(id)
  }

  // Back to the page it came from: the photo shrinks into its tile there (found again, even after stepping, and
  // brought into view if it's scrolled away) while the room fades. Opened on its own, it goes to its album.
  const close = () => {
    if (leaving) return
    const el = image.current
    const back = () => {
      coverPage(null)
      const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0
      if (over && idx > 0) navigate(-1)
      else navigate(albumTo)
    }
    if (!el || !smooth()) return back()
    setLeaving(true)
    coverPage('closing')
    const now = el.style.transform
    for (const a of el.getAnimations()) a.cancel()
    el.style.transform = ''
    const landed = el.getBoundingClientRect()
    const tile = over ? tileOf(photo.id) : null
    if (tile) {
      const seen = tile.getBoundingClientRect()
      if (seen.bottom < 0 || seen.top > innerHeight) tile.scrollIntoView({ block: 'center' })
    }
    const target = tile ? fromTile({ rect: tile.getBoundingClientRect(), radius: tileRadius(tile) }, landed) : null
    const from = { transform: now || 'none', clipPath: shown(el) }
    el.style.transform = now
    // Its tile stays empty until the photo has landed in it, then the page has it back.
    if (tile) tile.style.visibility = 'hidden'
    el.animate(
      target
        ? [from, target]
        : [
            { ...from, opacity: 1 },
            { transform: `${now} scale(0.9)`, clipPath: from.clipPath, opacity: 0 },
          ],
      { duration: target ? 240 : 180, easing: target ? ENTER : IN_SOFT, fill: 'forwards' },
    ).onfinish = () => {
      if (tile) tile.style.visibility = ''
      back()
    }
  }

  // Locked behind it: the page doesn't scroll. Over a page, that page is hidden once the photo has opened.
  useEffect(() => {
    const root = document.documentElement
    const was = root.style.overflow
    root.style.overflow = 'hidden'
    return () => {
      root.style.overflow = was
      coverPage(null)
    }
  }, [])

  // Opening: the photo grows from where it sat on the page, from the copy already showing there.
  useLayoutEffect(() => {
    const el = image.current
    const from = takeOrigin(photo.id)
    if (!over) return
    if (!el || !from || !smooth()) return coverPage('open')
    coverPage('opening')
    setStand(from.src)
    // Lifted out of its tile, which stays empty under it until it's covered.
    const tile = tileOf(photo.id)
    if (tile) tile.style.visibility = 'hidden'
    const play = el.animate([fromTile(from, el.getBoundingClientRect()), { transform: 'none', clipPath: shown(el) }], {
      duration: 300,
      easing: ENTER,
    })
    const done = () => {
      if (tile) tile.style.visibility = ''
      coverPage('open')
    }
    play.onfinish = done
    return () => {
      play.cancel()
      done()
    }
    // Only as it opens; stepping slides instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // A new photo: in from the side it was stepped towards, right behind the one leaving. A tap or a key slides them
  // quickly, starting at once; let go of mid-drag, they carry on at the finger's pace and ease in.
  useLayoutEffect(() => {
    const el = image.current
    const move = coming.current
    if (arriving || !el || !move) return
    coming.current = null
    setStand(null)
    const ghosts = [...(el.parentElement?.querySelectorAll<HTMLImageElement>('img[data-leaving]') ?? [])]
    const ghost = ghosts.at(-1)
    for (const old of ghosts.slice(0, -1)) old.remove()
    const apart = Math.max((move.away + el.offsetWidth) / 2 + GAP, 0)
    const shift = -move.direction * apart
    const timing = { duration: move.flung ? 200 : 240, easing: move.flung ? ENTER : SWIFT }
    el.animate([{ transform: `translateX(${move.from - shift}px)` }, { transform: 'none' }], timing)
    if (ghost)
      ghost.animate(
        [{ transform: `translateX(${move.from}px)` }, { transform: `translateX(${move.from + shift}px)` }],
        {
          ...timing,
          fill: 'forwards',
        },
      ).onfinish = () => ghost.remove()
  }, [photo.id, arriving])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLElement &&
        e.target.closest('input, textarea, select, [contenteditable], [role=dialog], [role=listbox]')
      )
        return
      if (e.key === 'ArrowLeft' && photo.previous) step(photo.previous, -1)
      if (e.key === 'ArrowRight' && photo.next) step(photo.next, 1)
      if (e.key === 'Escape' && !document.querySelector('[role=dialog], [role=alertdialog]')) close()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  // The photos either side are fetched ahead, what's known about them and their pictures, so stepping is instant.
  useEffect(() => {
    const size = matchMedia('(min-width: 1024px)').matches ? 'display' : 'medium'
    for (const ref of photo.around) new Image().src = albumPhotoUrl(ref, size)
    for (const id of [photo.previous, photo.next]) if (id) void queryClient.prefetchQuery(photoQuery(id))
  }, [photo.around, photo.previous, photo.next, queryClient])

  // A finger on the photo: sideways steps through the album, the photo following it; down (on a phone, with the
  // page at its top) pulls it away to close, the page behind showing through. Anything else scrolls, as usual.
  useEffect(() => {
    const surface = stage.current
    const el = image.current
    const page = room.current
    if (!surface || !el || !page) return
    let start: { x: number; y: number } | null = null
    let axis: 'x' | 'down' | 'scroll' | null = null
    let points: { t: number; x: number; y: number }[] = []
    const down = (e: TouchEvent) => {
      if (e.touches.length !== 1) return (start = null)
      const t = e.touches[0]!
      start = { x: t.clientX, y: t.clientY }
      axis = null
      points = []
    }
    const move = (e: TouchEvent) => {
      if (!start || e.touches.length !== 1) return
      const t = e.touches[0]!
      const dx = t.clientX - start.x
      const dy = t.clientY - start.y
      points.push({ t: performance.now(), x: t.clientX, y: t.clientY })
      if (!axis && Math.hypot(dx, dy) > 8) {
        if (Math.abs(dx) > Math.abs(dy)) axis = 'x'
        else if (dy > 0 && page.scrollTop <= 0 && smooth()) axis = 'down'
        else axis = 'scroll'
        for (const a of el.getAnimations()) a.finish()
        if (axis === 'down' && over) coverPage('closing')
      }
      if (axis === 'x') {
        e.preventDefault()
        if (!smooth()) return
        const edge = (dx > 0 && !photo.previous) || (dx < 0 && !photo.next)
        el.style.transform = `translateX(${edge ? rubber(dx, 80) : dx}px)`
      } else if (axis === 'down') {
        e.preventDefault()
        const pull = Math.max(0, dy)
        // It follows the finger anywhere, shrinking as it goes, as Pinterest's do.
        el.style.transform = `translate(${dx}px, ${pull}px) scale(${1 - Math.min(pull, 600) / 1200})`
        page.style.setProperty('--pull', String(Math.max(0, 1 - pull / 320)))
      }
    }
    const up = () => {
      if (!start) return
      const v = velocity(points)
      const last = points.at(-1)
      const dx = last ? last.x - start.x : 0
      const dy = last ? last.y - start.y : 0
      start = null
      if (axis === 'x') {
        const next = dx < 0 ? photo.next : photo.previous
        if (next && (Math.abs(dx) > el.clientWidth * 0.2 || Math.abs(v.x) > 0.45)) return step(next, dx < 0 ? 1 : -1)
        settle()
      } else if (axis === 'down') {
        if (dy > 100 || v.y > 0.5) return close()
        page.style.removeProperty('--pull')
        if (over) coverPage('open')
        settle()
      }
    }
    // Let go short of anything: back into place, with a little give.
    const settle = () => {
      if (!el.style.transform) return
      el.animate([{ transform: el.style.transform }, { transform: 'none' }], { duration: 240, easing: BOUNCE })
      el.style.transform = ''
    }
    surface.addEventListener('touchstart', down, { passive: true })
    surface.addEventListener('touchmove', move, { passive: false })
    surface.addEventListener('touchend', up)
    surface.addEventListener('touchcancel', up)
    return () => {
      surface.removeEventListener('touchstart', down)
      surface.removeEventListener('touchmove', move)
      surface.removeEventListener('touchend', up)
      surface.removeEventListener('touchcancel', up)
    }
  })

  // A mouse dragged sideways steps too, as it always has.
  const swipe = useRef<{ x: number; y: number } | null>(null)

  return (
    <article
      ref={room}
      aria-labelledby="photo-title"
      data-leaving={leaving || undefined}
      className="viewer-room fixed inset-0 z-50 overflow-y-auto overscroll-contain bg-(--room) text-foreground lg:flex lg:overflow-hidden lg:text-viewer-foreground"
    >
      {!over && <PageFrame photo={photo} title={where.title} to={albumTo} />}
      <div className="relative flex flex-col px-1 pt-[calc(var(--safe-top)+0.25rem)] lg:h-full lg:min-w-0 lg:flex-1 lg:p-0">
        <div className="viewer-chrome flex items-center gap-2 max-lg:pointer-events-none max-lg:absolute max-lg:inset-x-1 max-lg:top-[calc(var(--safe-top)+0.25rem)] max-lg:z-10 max-lg:p-2.5 lg:px-5 lg:pt-3 lg:pb-2">
          <Link
            to={albumTo}
            aria-label={`Back to ${where.title}`}
            onClick={(e) => {
              e.preventDefault()
              close()
            }}
            className={cn(round, 'hover:bg-viewer-foreground/12', floating)}
          >
            <X aria-hidden />
          </Link>
          <p className="min-w-0 flex-1 truncate text-center text-sm max-lg:invisible">
            <span className="font-medium">{where.title}</span>
            <span className="text-viewer-muted">
              {' '}
              · {photo.position} of {photo.of}
            </span>
          </p>
          <PhotoMenu photo={photo} title={title} />
        </div>
        <div
          ref={stage}
          className="relative min-h-0 flex-1 touch-pan-y select-none max-lg:aspect-(--ratio) max-lg:flex-none"
          style={{ '--ratio': `${photo.width} / ${photo.height}` } as CSSProperties}
          onPointerDown={(e) => (swipe.current = e.pointerType === 'mouse' ? { x: e.clientX, y: e.clientY } : null)}
          onPointerUp={(e) => {
            const from = swipe.current
            swipe.current = null
            if (!from) return
            const dx = e.clientX - from.x
            if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(e.clientY - from.y)) return
            step(dx < 0 ? photo.next : photo.previous, dx < 0 ? 1 : -1)
          }}
        >
          {/* The photo is fitted whole into the space it has, by its longer side: tall photos fill the height,
              wide ones the width. Nothing is cropped. On a phone or tablet that space is the photo's own shape. */}
          <div
            className="absolute inset-0 flex items-center justify-center lg:px-20 lg:pb-6"
            style={{ containerType: 'size' }}
          >
            <img
              ref={image}
              key={photo.ref}
              src={albumPhotoUrl(photo.ref, 'display')}
              srcSet={photoSrcSet(photo, ['medium', 'display'])}
              sizes={viewerSizes(photo)}
              width={photo.width}
              height={photo.height}
              alt={title}
              draggable={false}
              style={{
                // Its size is known before its picture arrives, so it can open into place at once.
                width: `min(100cqw, ${photo.width / photo.height} * 100cqh)`,
                aspectRatio: `${photo.width} / ${photo.height}`,
                ...(stand && { backgroundImage: `url("${stand}")`, backgroundSize: '100% 100%' }),
              }}
              className={cn(
                'h-auto max-w-none rounded-3xl object-contain will-change-transform lg:rounded-sm',
                arriving && 'opacity-0',
              )}
            />
          </div>
          {arriving && (
            <div className="absolute inset-0 grid place-items-center">
              <Loader label="Opening the photo" tone="quiet" />
            </div>
          )}
          <Step to={photo.previous} label="Previous photo" onGo={(id) => step(id, -1)} className="left-3 lg:left-5">
            <ChevronLeft aria-hidden />
          </Step>
          <Step to={photo.next} label="Next photo" onGo={(id) => step(id, 1)} className="right-3 lg:right-5">
            <ChevronRight aria-hidden />
          </Step>
        </div>
      </div>

      <aside className="viewer-details relative min-h-[45svh] bg-background pb-[calc(var(--safe-bottom)+2rem)] text-foreground lg:h-full lg:min-h-0 lg:w-[26rem] lg:shrink-0 lg:overflow-y-auto lg:pb-8 xl:w-[28rem]">
        <Details key={photo.id} photo={photo} title={title} guided={guided} onStep={go} />
      </aside>
    </article>
  )
}

// What can be done with the photo, behind one button on its top corner, as Pinterest's are: a sheet from the bottom
// on a phone, a menu on a computer. Download opens the original, exactly as it was added; cropping changes only the
// copies people see.
function PhotoMenu({ photo, title }: { photo: Photo; title: string }) {
  const [open, setOpen] = useState<'original' | 'album' | 'crop' | null>(null)
  const queryClient = useQueryClient()
  const shut = (now: boolean) => !now && setOpen(null)
  return (
    <>
      <ActionMenu
        title="Photo options"
        align="end"
        trigger={
          <button
            type="button"
            aria-label="Photo options"
            className={cn(round, 'hover:bg-viewer-foreground/12', floating)}
          >
            <Ellipsis className="size-6" aria-hidden />
          </button>
        }
        groups={[
          [
            { label: 'Download the original', icon: Download, onSelect: () => setOpen('original') },
            ...(photo.canEdit
              ? [
                  { label: 'Send to album', icon: FolderPlus, onSelect: () => setOpen('album') },
                  { label: 'Rotate, crop and color', icon: Crop, onSelect: () => setOpen('crop') },
                ]
              : []),
          ],
        ]}
      />
      <OriginalPhoto
        open={open === 'original'}
        onOpenChange={shut}
        src={albumPhotoUrl(photo.id, 'original')}
        fallback={albumPhotoUrl(photo.ref, 'display')}
        alt={title}
      />
      {photo.canEdit && <SendToAlbum photo={photo} open={open === 'album'} onOpenChange={shut} />}
      <CropPhoto
        open={open === 'crop'}
        onOpenChange={shut}
        source={albumPhotoUrl(photo.id, 'original')}
        crop={photo.crop}
        medium
        onSave={async (crop, copies) => {
          await saveCrop(`/album-photos/${photo.id}`, crop, copies)
          await afterPhotoEdit(queryClient)
        }}
      />
    </>
  )
}

// Sending the photo to an album, or to a new one made for it.
function SendToAlbum({
  photo,
  open,
  onOpenChange,
}: {
  photo: Photo
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const family = useFamily(photo.familyId)
  const [starting, setStarting] = useState(false)
  return (
    <>
      <SendPhotos
        open={open && !starting}
        onOpenChange={onOpenChange}
        familyId={photo.familyId}
        photos={[photo.id]}
        from={photo.album?.id}
        inAlbum={!!photo.album}
        onSent={() => onOpenChange(false)}
        onNewAlbum={() => setStarting(true)}
      />
      {starting && family.data && (
        <NewAlbum
          open
          onOpenChange={(now) => !now && setStarting(false)}
          family={family.data}
          space={null}
          collections={[]}
          first={false}
          send={[photo.id]}
        />
      )}
    </>
  )
}

function Step({
  to,
  label,
  onGo,
  className,
  children,
}: {
  to: string | null
  label: string
  onGo: (id: string | null) => void
  className: string
  children: ReactNode
}) {
  if (!to) return null
  return (
    <button
      type="button"
      aria-label={label}
      onClick={() => onGo(to)}
      className={cn(
        round,
        'absolute top-1/2 hidden -translate-y-1/2 bg-viewer/55 backdrop-blur-sm hover:bg-viewer/80 sm:grid',
        className,
      )}
    >
      {children}
    </button>
  )
}

// Who sees a photo, said plainly: wherever it's shared on its own, in any of your circles, and its album's people.
// Only whoever added it can change the first; sending it to an album adds the second.
function WhoSeesIt({ photo }: { photo: Photo }) {
  const queryClient = useQueryClient()
  const family = useFamily(photo.familyId)
  const [open, setOpen] = useState(false)
  const [who, setWho] = useState(() => audienceFrom(photo))
  const save = useMutation({
    mutationFn: () => api(`/album-photos/${photo.id}`, { method: 'PATCH', body: audienceBody(who) }),
    onSuccess: async () => {
      for (const key of ['photo', 'albums', 'found-photos', 'stream', 'home']) {
        await queryClient.invalidateQueries({ queryKey: [key] })
      }
      setOpen(false)
    },
  })
  const seen = whoSees(photo, family.data?.name ?? '')
  return (
    <p className="flex flex-wrap items-center gap-x-2">
      <span>Who sees it: {seen}</span>
      {photo.mine && family.data && (
        <>
          <button
            type="button"
            className="min-h-11 underline underline-offset-4 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
            onClick={() => setOpen(true)}
          >
            Change
          </button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-lg">
              <form
                className="flex flex-col gap-6"
                onSubmit={(e) => {
                  e.preventDefault()
                  save.mutate()
                }}
              >
                <DialogHeader>
                  <DialogTitle>Who sees it?</DialogTitle>
                  <DialogDescription>
                    {photo.album &&
                    (photo.albumShared?.everyone || photo.albumShared?.sharedWith.length || photo.albumSpace)
                      ? `${audienceSaid(photo.albumShared ?? { sharedWith: [] }, photo.albumSpace?.name ?? '')} also sees it while it’s in ${photo.album.title}.`
                      : 'Sending it to an album later adds the people the album is shared with.'}
                  </DialogDescription>
                </DialogHeader>
                <ShareWith here={photo.familyId} value={who} onChange={setWho} onlyMeHint="Just you" />
                <FormError error={save.error} />
                <Button type="submit" size="lg" disabled={save.isPending} className="self-start">
                  Save
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </>
      )}
    </p>
  )
}

const chip =
  'flex min-h-10 items-center gap-2 rounded-full bg-secondary text-sm outline-none focus-within:ring-[3px] focus-within:ring-ring/50'

function Details({
  photo,
  title,
  guided,
  onStep,
}: {
  photo: Photo
  title: string
  guided: boolean
  onStep: (id: string | null, caption?: boolean) => void
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(guided && photo.canEdit)
  const refresh = async () => {
    for (const key of ['photo', 'album', 'albums', 'found-photos', 'stream', 'person']) {
      await queryClient.invalidateQueries({ queryKey: [key] })
    }
  }
  const tag = useMutation({
    mutationFn: (change: Organize) => organize(photo.familyId, [photo.id], change),
    onSuccess: refresh,
  })
  const where = whereFrom(photo)
  const [sending, setSending] = useState(false)
  const cover = useMutation({
    mutationFn: () => api(`/albums/${photo.album?.id}`, { method: 'PATCH', body: { coverPhotoId: photo.id } }),
    onSuccess: refresh,
  })
  const remove = useMutation({
    mutationFn: () => api(`/album-photos/${photo.id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      for (const key of ['album', 'albums', 'found-photos', 'stream']) {
        await queryClient.invalidateQueries({ queryKey: [key] })
      }
      navigate(where.to, { replace: true })
    },
  })

  return (
    <div className="flex flex-col gap-8 px-6 pt-5 sm:px-8 lg:pt-10">
      <header className="flex flex-col gap-3">
        <Link to={where.to} className="inline-flex min-h-11 items-center self-start eyebrow hover:underline">
          {where.title}
        </Link>
        {!editing && (
          <h1 id="photo-title" className="text-title font-semibold tracking-tight text-balance">
            {photo.caption || <span className="text-muted-foreground">{title}</span>}
          </h1>
        )}
        {editing && (
          <h1 id="photo-title" className="sr-only">
            {title}
          </h1>
        )}
        {!editing && (
          <div className="flex flex-col gap-1 text-sub text-muted-foreground">
            <p className="flex items-center gap-2">
              <CalendarDays className="size-4 shrink-0" aria-hidden />
              {photo.takenOn ? `Taken ${showDate(photo.takenOn)}` : `Added ${addedOn(photo.createdAt)}`}
            </p>
            <p>Added by {photo.by}</p>
            <WhoSeesIt photo={photo} />
          </div>
        )}
        {!editing && (photo.canEdit || photo.albumMine) && (
          <div className="flex flex-wrap items-center gap-2">
            {photo.canEdit && (
              <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                Edit caption and date
              </Button>
            )}
            {photo.canEdit && (
              <Button variant="outline" size="sm" onClick={() => setSending(true)}>
                Send to album
              </Button>
            )}
            {photo.albumMine &&
              photo.album &&
              (photo.cover ? (
                <p className="flex min-h-8 items-center gap-1.5 px-2 text-sm text-muted-foreground">
                  <Check className="size-4" aria-hidden /> The album’s cover
                </p>
              ) : (
                <Button variant="outline" size="sm" disabled={cover.isPending} onClick={() => cover.mutate()}>
                  Make it the album cover
                </Button>
              ))}
          </div>
        )}
        <FormError error={cover.error} />
        <SendToAlbum photo={photo} open={sending} onOpenChange={setSending} />
      </header>

      {editing && (
        <EditPhoto
          photo={photo}
          guided={guided}
          onDone={(then) => {
            setEditing(false)
            if (then === 'next') onStep(photo.next)
            if (then === 'finish') onStep(photo.id, false)
          }}
          onSaved={refresh}
        />
      )}

      <section className="flex flex-col gap-3" aria-labelledby="in-this-photo">
        <h2 id="in-this-photo" className="eyebrow">
          In this photo
        </h2>
        {photo.people.length > 0 ? (
          <ul className="flex flex-wrap gap-2">
            {photo.people.map((p) => (
              <li key={p.id} className={cn(chip, 'pl-1')}>
                <Avatar person={p} className="size-8 text-sm shadow-none" />
                <Link to={`/person/${p.id}`} className="font-medium underline-offset-4 outline-none hover:underline">
                  {p.name}
                </Link>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-9"
                  aria-label={`Untag ${p.name}`}
                  disabled={tag.isPending}
                  onClick={() => tag.mutate({ removePeople: [p.id] })}
                >
                  <X aria-hidden />
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground">No one is tagged yet.</p>
        )}
        <TagSomeone photo={photo} pending={tag.isPending} onTag={(id) => tag.mutate({ addPeople: [id] })} />
      </section>

      <section className="flex flex-col gap-3" aria-labelledby="photo-tags">
        <h2 id="photo-tags" className="eyebrow">
          Tags
        </h2>
        {photo.tags.length > 0 && (
          <ul className="flex flex-wrap gap-2">
            {photo.tags.map((t) => (
              <li key={t} className={cn(chip, 'pl-4')}>
                <Link
                  to={`/circle/${photo.familyId}/photos?${new URLSearchParams({ tag: t })}`}
                  className="underline-offset-4 outline-none hover:underline"
                >
                  {t}
                </Link>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-9"
                  aria-label={`Remove the tag ${t}`}
                  disabled={tag.isPending}
                  onClick={() => tag.mutate({ removeTags: [t] })}
                >
                  <X aria-hidden />
                </Button>
              </li>
            ))}
          </ul>
        )}
        <AddTags pending={tag.isPending} onAdd={(tags) => tag.mutate({ addTags: tags })} />
      </section>
      <FormError error={tag.error} />

      <footer className="flex flex-col gap-4 border-t pt-6">
        <div className="flex flex-col gap-1">
          <OriginalPhoto
            src={albumPhotoUrl(photo.id, 'original')}
            fallback={albumPhotoUrl(photo.ref, 'display')}
            alt={title}
          >
            <button
              type="button"
              className="flex items-center gap-2 self-start text-sm underline underline-offset-4 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
            >
              <Download className="size-4" aria-hidden /> Open the original
            </button>
          </OriginalPhoto>
          <p className="whisper">Kept exactly as it was added.</p>
        </div>
        {photo.canEdit && (
          <>
            <FormError error={remove.error} />
            <ConfirmButton
              label="Delete this photo"
              title="Delete this photo?"
              description="It will be removed for everyone it’s shared with."
              confirm="Delete photo"
              disabled={remove.isPending}
              onConfirm={() => remove.mutate()}
            />
          </>
        )}
      </footer>
    </div>
  )
}

// Choosing someone tags them straight away; the list then leaves them out.
function TagSomeone({ photo, pending, onTag }: { photo: Photo; pending: boolean; onTag: (id: string) => void }) {
  const tree = useTree(photo.familyId)
  const untagged = (tree.data?.people ?? []).filter((p) => !photo.people.some((x) => x.id === p.id))
  if (untagged.length === 0) return null
  return (
    <PersonSelect
      aria-label="Tag someone in this photo"
      placeholder={pending ? 'Tagging…' : 'Tag someone'}
      people={untagged}
      value=""
      onChange={(id) => id && onTag(id)}
    />
  )
}

function AddTags({ pending, onAdd }: { pending: boolean; onAdd: (tags: string[]) => void }) {
  const [text, setText] = useState('')
  const tags = splitTags(text)
  return (
    <form
      className="flex gap-2"
      onSubmit={(e) => {
        e.preventDefault()
        if (tags.length) onAdd(tags)
        setText('')
      }}
    >
      <Input aria-label="Add tags" value={text} onChange={(e) => setText(e.target.value)} />
      <Button type="submit" variant="outline" disabled={pending || tags.length === 0}>
        <Plus aria-hidden /> Add
      </Button>
    </form>
  )
}

function EditPhoto({
  photo,
  guided,
  onDone,
  onSaved,
}: {
  photo: Photo
  guided: boolean
  onDone: (then?: 'next' | 'finish') => void
  onSaved: () => Promise<void>
}) {
  const [caption, setCaption] = useState(photo.caption)
  const [date, setDate] = useState(photo.takenOn)
  const save = useMutation({
    mutationFn: async (then: 'next' | 'finish' | undefined) => {
      await api(`/album-photos/${photo.id}`, {
        method: 'PATCH',
        body: { caption: caption.trim(), takenOn: date },
      })
      return then
    },
    onSuccess: async (then) => {
      await onSaved()
      onDone(then)
    },
  })
  const next = guided && photo.next
  function submit(e: FormEvent) {
    e.preventDefault()
    save.mutate(next ? 'next' : guided ? 'finish' : undefined)
  }
  return (
    <form onSubmit={submit} className="flex flex-col gap-4 rounded-3xl bg-secondary p-5">
      {guided && (
        <p className="eyebrow">
          Captions · photo {photo.position} of {photo.of}
        </p>
      )}
      <div className="grid gap-2">
        <Label htmlFor="photo-caption">Caption</Label>
        <Textarea
          id="photo-caption"
          maxLength={2000}
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          className="bg-background"
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="photo-date">Taken on</Label>
        <DatePicker id="photo-date" value={date} onChange={setDate} aria-describedby="photo-date-hint" />
        <p id="photo-date-hint" className="text-sub text-muted-foreground">
          A year is enough if that’s all anyone knows.
        </p>
      </div>
      <FormError error={save.error} />
      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={save.isPending}>
          {next ? 'Save and next' : 'Save'}
        </Button>
        {next ? (
          <>
            <Button type="button" variant="ghost" disabled={save.isPending} onClick={() => onDone('next')}>
              Skip
            </Button>
            <Button type="button" variant="ghost" disabled={save.isPending} onClick={() => onDone('finish')}>
              Finish
            </Button>
          </>
        ) : (
          <Button type="button" variant="ghost" onClick={() => onDone(guided ? 'finish' : undefined)}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  )
}
